// ==================== CAMPAIGNS SERVICE ====================

import { apiService } from './api';

// ==================== TYPES ====================

export type CampaignStatus = 'DRAFT' | 'SCHEDULED' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'CANCELLED' | 'FAILED';
export type CampaignAudienceType = 'ALL_CONTACTS' | 'SELECTED_GROUPS' | 'TAG_FILTER' | 'MANUAL';

export interface CampaignTemplate {
  id: string;
  name: string;
  category: string;
  status: string;
  bodyText?: string;
  headerType?: string;
  headerContent?: string;
  footerText?: string;
  buttons?: unknown[];
}

export interface CampaignStats {
  total: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  pending: number;
}

export interface Campaign {
  id: string;
  name: string;
  description?: string;
  status: CampaignStatus;
  scheduledAt?: string;
  startedAt?: string;
  completedAt?: string;
  pausedAt?: string;
  batchSize: number;
  batchDelayMs: number;
  maxPerSecond: number;
  testMode: boolean;
  testRecipients: string[];
  templateVariables?: Record<string, string>;
  audienceType: CampaignAudienceType;
  groupIds: string[];
  tagFilters: string[];
  contactIds?: string[]; // Contact IDs for MANUAL audience type
  stats: CampaignStats;
  lastError?: string;
  errorCount: number;
  template?: CampaignTemplate;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCampaignData {
  name: string;
  description?: string;
  templateId: string;
  scheduledAt?: string;
  batchSize?: number;
  batchDelayMs?: number;
  maxPerSecond?: number;
  testMode?: boolean;
  testRecipients?: string[];
  templateVariables?: Record<string, string>;
  audienceType?: CampaignAudienceType;
  groupIds?: string[];
  tagFilters?: string[];
  contactIds?: string[];
}

export type UpdateCampaignData = Partial<CreateCampaignData>;

export interface CampaignQueryParams {
  search?: string;
  status?: CampaignStatus;
  templateId?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CampaignListResponse {
  items: Campaign[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage?: boolean;
    hasPrevPage?: boolean;
  };
}

export interface CampaignStatsData {
  total: number;
  byStatus: {
    draft: number;
    scheduled: number;
    running: number;
    paused: number;
    completed: number;
    cancelled: number;
    failed: number;
  };
  messageStats: {
    totalRecipients: number;
    sent: number;
    delivered: number;
    read: number;
    failed: number;
  };
}

export interface CampaignContact {
  id: string;
  status: string;
  sentAt?: string;
  deliveredAt?: string;
  readAt?: string;
  failedAt?: string;
  errorMessage?: string;
  contact: {
    id: string;
    phoneNumber: string;
    name?: string;
  };
}

// ==================== SERVICE ====================

export const campaignsService = {
  async getAll(params?: CampaignQueryParams): Promise<CampaignListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.templateId) queryParams.append('templateId', params.templateId);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const url = queryString ? `/customer/campaigns?${queryString}` : '/customer/campaigns';

    const response = await apiService.get(url);
    const responseData = response.data as { data?: Campaign[]; meta?: CampaignListResponse['meta'] } | Campaign[];
    
    // Extract items and meta from nested response
    const items = Array.isArray(responseData) 
      ? responseData 
      : (responseData?.data || []);
    
    // Meta can be at different levels depending on API response structure
    const meta = !Array.isArray(responseData) && responseData?.meta
      ? responseData.meta
      : { total: items.length, page: 1, limit: 10, totalPages: 1 };
    
    return { items, meta };
  },

  async getOne(id: string): Promise<Campaign> {
    const response = await apiService.get<{ data: Campaign }>(`/customer/campaigns/${id}`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch campaign');
    }
    // The backend returns { data: campaign }, so we need to extract it
    const data = response.data as { data: Campaign } | Campaign;
    return 'data' in data && data.data ? data.data : data as Campaign;
  },

  async getStats(): Promise<CampaignStatsData> {
    const response = await apiService.get<CampaignStatsData>('/customer/campaigns/stats');
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch campaign stats');
    }
    return response.data as CampaignStatsData;
  },

  async getContacts(
    campaignId: string,
    params?: { page?: number; limit?: number; status?: string }
  ): Promise<{ items: CampaignContact[]; meta: { total: number; page: number; limit: number; totalPages: number } }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const url = queryString 
      ? `/customer/campaigns/${campaignId}/contacts?${queryString}` 
      : `/customer/campaigns/${campaignId}/contacts`;

    const response = await apiService.get(url);
    const responseData = response.data as { data?: CampaignContact[]; meta?: { total: number; page: number; limit: number; totalPages: number } } | CampaignContact[];
    
    return {
      items: Array.isArray(responseData) 
        ? responseData 
        : (responseData?.data || []),
      meta: (response as { meta?: { total: number; page: number; limit: number; totalPages: number } }).meta || 
            (responseData as { meta?: { total: number; page: number; limit: number; totalPages: number } })?.meta || 
            { total: 0, page: 1, limit: 20, totalPages: 0 },
    };
  },

  async create(data: CreateCampaignData): Promise<Campaign> {
    const response = await apiService.post<Campaign>('/customer/campaigns', data);
    if (!response.success) {
      throw new Error(response.message || 'Failed to create campaign');
    }
    return response.data as Campaign;
  },

  async update(id: string, data: UpdateCampaignData): Promise<Campaign> {
    const response = await apiService.patch<Campaign>(`/customer/campaigns/${id}`, data);
    if (!response.success) {
      throw new Error(response.message || 'Failed to update campaign');
    }
    return response.data as Campaign;
  },

  async delete(id: string): Promise<void> {
    const response = await apiService.delete<void>(`/customer/campaigns/${id}`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete campaign');
    }
  },

  async start(id: string): Promise<Campaign> {
    const response = await apiService.post<Campaign>(`/customer/campaigns/${id}/start`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to start campaign');
    }
    return response.data as Campaign;
  },

  async pause(id: string): Promise<Campaign> {
    const response = await apiService.post<Campaign>(`/customer/campaigns/${id}/pause`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to pause campaign');
    }
    return response.data as Campaign;
  },

  async cancel(id: string): Promise<Campaign> {
    const response = await apiService.post<Campaign>(`/customer/campaigns/${id}/cancel`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to cancel campaign');
    }
    return response.data as Campaign;
  },
};
